import { isCanSetVerifyMechanismGames, isForceVerifyMechanismGames } from 'config/promoGameTypes';
import { useSelector } from 'react-redux';
import { createSelector } from 'reselect';

const selectState = createSelector(
    [
        s => s.promo.type,
        s => s.settingPromo?.enableVerifyMechanism,
        s => s.signUpForm.id,
        s => s.lotteryTasks,
        s => s.userWinnerPrize,
        s => s.userGameResultRecords,
        s => s.flags,
        s => s.mgmReferral
    ],
    (
        gameType,
        enableVerifyMechanism,
        signUpFormId,
        lotteryTasks,
        userWinnerPrize,
        userGameResultRecords = [],
        flags,
        mgmReferral
    ) => {
        const { isMgmShare, isMgmSelf } = flags;
        const {
            lotteryTasks: mgmLotteryTasks,
            userGameResultRecords: mgmUserGameResultRecords,
            userWinnerPrize: mgmUserWinnerPrize
        } = mgmReferral;
        const currentLotteryTasks = { ...lotteryTasks, ...mgmLotteryTasks };
        const mgmUserGameResultRecordsAddTag = mgmUserGameResultRecords.map(obj => {
            return { ...obj, isMgm: true };
        });
        const isAllGameResultRecordsBeDeleted = userGameResultRecords.every(
            obj => obj.deletedAt !== null
        );

        const currentGameResultRecords = [
            ...userGameResultRecords,
            ...mgmUserGameResultRecordsAddTag
        ];
        const currentWinnerPrize = {
            winnerPrizes: [...userWinnerPrize.winnerPrizes, ...mgmUserWinnerPrize.winnerPrizes],
            thankPrizes: [...userWinnerPrize.thankPrizes, ...mgmUserWinnerPrize.thankPrizes],
            unOpenWinnerPrize: [
                ...userWinnerPrize.unOpenWinnerPrize,
                ...mgmUserWinnerPrize.unOpenWinnerPrize
            ]
        };
        const { winnerPrizes, thankPrizes, unOpenWinnerPrize } = currentWinnerPrize;
        const allWinners = [...winnerPrizes, ...thankPrizes, ...unOpenWinnerPrize];
        const hasEnableSignUpForm = Boolean(signUpFormId);
        const normalizedUserGameResultRecords = currentGameResultRecords.reduce(
            (obj, record) => {
                if (Boolean(record.isMgm)) {
                    obj.allUUIDsForMgm.push(record.uuid);
                } else {
                    obj.allUUIDs.push(record.uuid);
                }
                const hasGotLotteryTaskWinner =
                    allWinners.find(
                        w =>
                            w.entryUuid === record.uuid &&
                            currentLotteryTasks[w.prize.lotteryUuid] !== undefined
                    ) !== undefined;
                obj.byUUIDs[record.uuid] = record;
                if (
                    isEntryValidToGetTaskWinner(record, {
                        enableVerifyMechanism,
                        gameType,
                        hasEnableSignUpForm,
                        isMgm: Boolean(isMgmSelf) || Boolean(isMgmShare),
                        userGameResultRecords
                    })
                ) {
                    obj.validEntryTotal += 1;
                    if (!hasGotLotteryTaskWinner) {
                        obj.allUUIDsValidToGetTaskWinner.push(record.uuid);
                    }
                }
                return obj;
            },
            {
                allUUIDs: [],
                allUUIDsForMgm: [],
                allUUIDsValidToGetTaskWinner: [],
                validEntryTotal: 0,
                byUUIDs: {}
            }
        );

        return {
            entriesRecords: normalizedUserGameResultRecords,
            lotteriesTaskWinnerInfo: Object.keys(currentLotteryTasks).reduce((obj, uuid) => {
                const anyWinnerOfLottery = allWinners.find(w => w.prize.lotteryUuid === uuid);
                const entryData = (() => {
                    if (Boolean(anyWinnerOfLottery)) {
                        const { uuid, flow = {}, deletedAt = '' } =
                            normalizedUserGameResultRecords.byUUIDs[anyWinnerOfLottery.entryUuid] ??
                            {};
                        return { entryUUID: uuid, entryFlow: { ...flow, deletedAt } };
                    } else {
                        return { entryUUID: '', entryFlow: {} };
                    }
                })();
                obj[uuid] = {
                    hasGetWinner: Boolean(anyWinnerOfLottery),
                    ...entryData
                };
                return obj;
            }, {}),
            isAllGameResultRecordsBeDeleted: isAllGameResultRecordsBeDeleted
        };
    }
);

export default function useLotteriesTaskUtilsState() {
    return useSelector(selectState);
}

// 根據 user 參加紀錄檢查每一張關卡獎卡片是否能領獎()
function isEntryValidToGetTaskWinner(
    entry,
    { enableVerifyMechanism, gameType, hasEnableSignUpForm, isMgm = false, userGameResultRecords }
) {
    const {
        deletedAt,
        flow: { qualified, formSignUp },
        hasGotLotteryTaskWinner
    } = entry;
    //For mgm_share 要拿原模組的 userGameResultRecords，但這樣寫就沒辦法參加很多次，如以後有需求，需要再進行變更
    const { flow: { formSignUp: primaryFormSignUp } = {} } = userGameResultRecords[0];
    /* 此 entry：
    1. 沒有已經領過獎
    2. 沒有被刪除
    3. 沒有需要填表 or 有填過表
    4. 沒有需要審核 or 有資格
    */
    if (
        [
            !hasGotLotteryTaskWinner,
            !deletedAt,
            !hasEnableSignUpForm || Boolean(formSignUp) || (isMgm && Boolean(primaryFormSignUp)),
            !(
                isForceVerifyMechanismGames(gameType) ||
                (isCanSetVerifyMechanismGames(gameType) && Boolean(enableVerifyMechanism))
            ) || Boolean(qualified)
        ].every(e => e)
    ) {
        return true;
    } else {
        return false;
    }
}
